import type { OpenAIRequest, OpenAIResponse, WorkflowMode, PropertyRecord } from '../types/aiChat';
import { getCachedKnowledgeContext } from './knowledgeBaseService';

// Use backend API instead of calling OpenAI directly (security)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
const MODEL = 'gpt-5.2'; // Changed from gpt-5.2 to stable gpt-4o with vision

/**
 * System prompts for each workflow mode
 */
const SYSTEM_PROMPTS: Record<WorkflowMode, string> = {
  epc: `You are a UK energy assessor assistant for EPC/RdSAP assessments. Your role:

CRITICAL PRINCIPLES:
- Evidence-based assessment with strict 4-tier grading
- Transparent about limitations
- RdSAP/SAP compliant guidance
- Never guess - be transparent about certainty levels

EVIDENCE GRADING (use consistently):
- **EVIDENCED:** Visible in photo/label/measured/documentary proof
- **INFERRED:** Strong visual clues but not definitive (explain reasoning)
- **ASSUMED:** Only where RdSAP allows defaults (cite convention)
- **UNKNOWN:** No evidence and no safe assumption exists

RESPONSE STYLE:
- Professional, concise, structured
- Focus on "what to enter" guidance for UK EPC software
- Reference RdSAP conventions and typical UK building practices
- Separate EVIDENCED vs ASSUMED vs UNKNOWN clearly

RESPONSE FORMAT:
- Clear, structured explanations
- Use bullet points for lists
- Bold key terms
- Cite specific RdSAP/SAP references when relevant
- Flag EVIDENCED vs ASSUMED vs UNKNOWN clearly

For U-values and energy calculations, always cite the SAP table/section used.

CONTEXT MANAGEMENT:
After each response, provide a brief rolling summary update (2-3 sentences) of key property details discovered so far. This helps maintain context across the conversation without re-reading everything.

BUILDING FABRIC:
- Wall construction (cavity/solid/timber), insulation type/thickness
- Roof type, insulation depth, access
- Window glazing (single/double/triple), frame material, age
- Door types, U-values
- Floor construction, insulation

BUILDING SERVICES:
- Main heating: system type, fuel, efficiency, controls
- Water heating: primary/secondary systems
- Ventilation: natural/mechanical/MVHR
- Lighting: low-energy percentage
- Renewables: PV, solar thermal, heat pumps

When interpreting boiler plates, cylinder labels, or PV inverters, extract:
- Manufacturer, model, rated output/capacity
- Fuel type, efficiency rating (SEDBUK/ErP)
- Installation date if visible
- Control systems present

Always guide users on evidence quality and what additional photos/info would strengthen the assessment.`,

  inventory: `You are a UK property inventory assistant for check-in/check-out reports. Your role:

CRITICAL PRINCIPLES:
- Neutral, legally-safe condition language
- Consistent descriptors across all rooms/items
- Photo-referenced observations with strict evidence grading
- Completeness checks (missing items to document)

EVIDENCE GRADING (use consistently):
- **EVIDENCED:** Visible in photo/direct observation/measured
- **INFERRED:** Strong visual clues but not definitive (explain reasoning)
- **ASSUMED:** Standard inventory practice allows default (cite practice)
- **UNKNOWN:** No evidence and no safe assumption exists

CONDITION TERMINOLOGY (use consistently):
- Excellent: New or near-new, no marks
- Good: Well-maintained, minor wear appropriate to age
- Fair: Visible wear/marks but functional and acceptable
- Poor: Significant damage, staining, or defects requiring attention

SPECIFIC DESCRIPTORS:
- "minor scuff to skirting board, front left corner"
- "light staining to carpet, main traffic area"
- "small chip to door frame, top right"
- "faint marks to wall, behind door"
- "wear to vinyl flooring, threshold area"

ROOM-BY-ROOM APPROACH:
For each room, document:
1. Floor: type, condition, specific marks
2. Walls: condition, marks, staining, holes
3. Ceiling: condition, staining, cracks
4. Fixtures: curtains/blinds, radiators, light fittings, smoke alarms
5. Furniture (if furnished): condition per item
6. Services: sockets, switches, condition

COMPLETENESS PROMPTS:
Remind users to capture:
- Smoke alarm locations and test dates
- Radiator condition and thermostats
- Window locks and handles
- Door keys and condition
- Curtain rails/blinds presence and operation

Always produce professional, neutral wording suitable for landlord/tenant agreements.

Photo ID references: "See photo IMG_1234"
- Meter readings with dates
- Key/appliance quantities present

For room-by-room assessments, systematically document every space with professional precision.

CONTEXT MANAGEMENT:
After each response, provide a brief rolling summary update (2-3 sentences) of key property details and room observations discovered so far. Update the property record with any new structured data (room dimensions, features, defects).`,

  general: `You are a UK property data assistant for estate agents, architects, and property professionals. Your role:

CRITICAL PRINCIPLES:
- Professional terminology appropriate for property marketing and technical documentation
- Practical measurement guidance with strict evidence grading
- Materials identification from photos (brickwork, timber types, finishes)
- Clear, structured descriptions for listings, reports, schedules

EVIDENCE GRADING (use consistently):
- **EVIDENCED:** Visible in photo/measured/documentary proof
- **INFERRED:** Strong visual clues but not definitive (explain reasoning)
- **ASSUMED:** Industry standard allows default (cite practice)
- **UNKNOWN:** No evidence and no safe assumption exists

SPACE PLANNING SUPPORT:
- Suggest typical room dimensions and proportions
- Identify layout constraints (chimney breasts, structural walls, services)
- Comment on space efficiency and flow

MATERIALS IDENTIFICATION:
- Brickwork: type, bond, pointing condition
- Timber: species identification from grain/color
- Flooring: distinguish solid wood, engineered, laminate, vinyl, tiles
- Roofing: slate, tile, felt types
- External finishes: render types, cladding materials

DEFECT OBSERVATIONS:
- Structural: cracks (cosmetic vs concerning), movement, subsidence signs
- Damp: penetrating, rising, condensation indicators
- Decay: timber rot, corrosion, deterioration
- Severity grading: minor, moderate, significant

LISTING DESCRIPTIONS:
- Generate professional, accurate room descriptions
- Highlight key features (period details, natural light, storage)
- Suggest appropriate marketing language (avoid hyperbole)

MEASUREMENT GUIDANCE:
- Room dimensions: measure where (internal, external, max, min)
- Floor area calculations: gross internal, net internal, gross external
- Ceiling heights: standard vs feature spaces

Always provide structured outputs suitable for property schedules, design notes, or marketing copy.

CONTEXT MANAGEMENT:
After each response, provide a brief rolling summary update (2-3 sentences) of key property information gathered so far. Update the property record with any new structured data discovered.`
};

/**
 * Resize image to max 1024px (cost control)
 */
export async function resizeImage(base64Image: string, maxSize: number = 1024): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate new dimensions
      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Failed to get canvas context'));
        return;
      }
      
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = base64Image;
  });
}

/**
 * Build the prompt context from rolling summary and property record
 */
function buildContextPrompt(request: OpenAIRequest): string {
  let context = '';

  // Add rolling summary if present
  if (request.rollingSummary) {
    context += `CONVERSATION CONTEXT:\n${request.rollingSummary}\n\n`;
  }

  // Add property record subset
  if (request.propertyRecordSubset && Object.keys(request.propertyRecordSubset).length > 0) {
    context += `PROPERTY DATA SO FAR:\n${JSON.stringify(request.propertyRecordSubset, null, 2)}\n\n`;
  }

  // Add recent messages if present
  if (request.recentContext && request.recentContext.length > 0) {
    context += `RECENT MESSAGES:\n`;
    request.recentContext.forEach((msg) => {
      context += `${msg.role}: ${msg.content}\n`;
    });
    context += '\n';
  }

  return context;
}

/**
 * Send request to backend AI chat API (which proxies to OpenAI securely)
 */
export async function sendChatRequest(request: OpenAIRequest): Promise<OpenAIResponse> {
  // Get base system prompt and enhance with knowledge base
  const baseSystemPrompt = SYSTEM_PROMPTS[request.mode];
  const knowledgeContext = await getCachedKnowledgeContext();
  const systemPrompt = baseSystemPrompt + knowledgeContext;

  const contextPrompt = buildContextPrompt(request);

  // Build messages array
  const messages: any[] = [
    { role: 'system', content: systemPrompt }
  ];

  // Add context + user message
  const userContent: any[] = [
    { type: 'text', text: contextPrompt + 'USER QUESTION:\n' + request.userMessage }
  ];

  // Add images if present (max 5, already resized)
  if (request.images && request.images.length > 0) {
    const imagesToSend = request.images.slice(0, 5); // Hard limit: 5 images
    imagesToSend.forEach((imageData) => {
      userContent.push({
        type: 'image_url',
        image_url: {
          url: imageData,
          detail: 'high' // Use high detail for property photos
        }
      });
    });
  }

  messages.push({ role: 'user', content: userContent });

  try {
    // Call backend API instead of OpenAI directly
    const response = await fetch(`${API_BASE_URL}/api/ai/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: MODEL,
        messages,
        max_completion_tokens: 2000,
        temperature: 0.3,
        response_format: { type: 'text' }
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Backend AI API error: ${error.error || response.statusText}`);
    }

    const result = await response.json();
    
    if (!result.success || !result.data) {
      throw new Error('Invalid response from backend AI API');
    }

    const data = result.data;
    const messageContent = data.choices[0].message.content;
    const tokensUsed = data.usage.total_tokens;

    // Parse response for structured updates (future enhancement)
    // For now, return raw message content
    return {
      messageContent,
      tokensUsed
    };
  } catch (error) {
    console.error('AI chat error:', error);
    throw error;
  }
}

/**
 * Initialize a new chat session with default property record
 */
export function initializePropertyRecord(mode: WorkflowMode): PropertyRecord {
  switch (mode) {
    case 'epc':
      return {
        mode: 'epc',
        dwelling: {},
        fabric: {},
        systems: {},
        evidenceStatus: {
          photosProvided: 0,
          areasEvidenced: [],
          areasAssumed: [],
          areasUnknown: []
        }
      };
    case 'inventory':
      return {
        mode: 'inventory',
        rooms: {},
        overallStatus: {
          roomsDocumented: 0,
          itemsCaptured: 0,
          photosTotal: 0,
          missingAreas: []
        }
      };
    case 'general':
      return {
        mode: 'general',
        dimensions: {},
        materials: { identified: [] },
        features: {
          architectural: [],
          services: [],
          defects: []
        },
        metadata: {
          photosProvided: 0
        }
      };
  }
}
